import { Form, Formik, FormikProps } from "formik"
import * as Yup from "yup"
import {
    AgreementOptions,
    BusinessType,
    EntityHistory,
    InvoiceType,
} from "../../../../../support/admin/entities/supportEntitiesTypes"
import { forwardRef, Ref } from "react"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { AccountFromValues } from "../accountTypes"
import { AccountUpdateContent } from "./AccountUpdateContent"

type EditAccountFormProps = Readonly<{
    initialValues?: AccountFromValues
    history?: EntityHistory
    onSubmit: (formData: AccountFromValues) => void
}>

const defaultInitialValues: AccountFromValues = {
    businessType: "",
    address1: "",
    address2: "",
    city: "",
    name: "",
    businessNumber: "",
    contactPerson: "",
    email: "",
    countryCode: "",
    isSystemOwner: "",
    postalCode: "",
    telephone: "",
    isActive: true,
    eanNumber: "",
    invoiceType: "",
    attention: "",
    invoiceEmail: "",
    reminderEmail: "",
    poNumber: "",
}

type FormikRef = Ref<FormikProps<AccountFromValues>>

export const AccountUpdateForm = forwardRef(
    ({ initialValues, onSubmit, history }: EditAccountFormProps, ref: FormikRef) => {
        const { l10n } = useAppLocalization()

        const supplierSchema = Yup.object().shape({
            isActive: Yup.boolean(),
            businessType: Yup.string()
                .oneOf(
                    Object.values(BusinessType),
                    l10n.getString("support-create-entity-error-label-company-invalid-type"),
                )
                .required()
                .label(l10n.getString("support-create-entity-error-label-company-type")),
            businessNumber: Yup.string()
                .required()
                .label(l10n.getString("supplier-update-account-error-label-registration-number")),
            name: Yup.string().required().label(l10n.getString("support-create-entity-error-label-company-name")),
            countryCode: Yup.string().required().label(l10n.getString("support-create-entity-error-label-country")),
            address1: Yup.string().required().label(l10n.getString("support-create-entity-error-label-address")),
            address2: Yup.string().label(l10n.getString("support-create-entity-error-label-address")),
            postalCode: Yup.string().required().label(l10n.getString("support-create-entity-error-label-postal-code")),
            city: Yup.string().required().label(l10n.getString("support-create-entity-error-label-city")),
            contactPerson: Yup.string()
                .required()
                .label(l10n.getString("support-create-entity-error-label-contact-person")),
            email: Yup.string().email().required().label(l10n.getString("support-create-entity-error-label-email")),
            telephone: Yup.string().required().label(l10n.getString("support-create-entity-error-label-telephone")),
            invoiceType: Yup.string()
                .label(l10n.getString("support-create-entity-error-label-invoice-type"))
                .when("isSystemOwner", {
                    is: (val: AgreementOptions) => val === AgreementOptions.YES,
                    then: (schema) => schema.required(),
                    otherwise: (schema) => schema.optional(),
                }),
            eanNumber: Yup.string()
                .label(l10n.getString("support-create-entity-error-label-ean-number"))
                .when("invoiceType", {
                    is: (val: InvoiceType) => val === InvoiceType.EAN_NUMBER,
                    then: (schema) => schema.required(),
                    otherwise: (schema) => schema.optional(),
                }),
            invoiceEmail: Yup.string()
                .label(l10n.getString("support-create-entity-error-label-invoice-email"))
                .when("invoiceType", {
                    is: (val: InvoiceType) => val === InvoiceType.EMAIL,
                    then: (schema) => schema.required().email(),
                    otherwise: (schema) => schema.optional(),
                }),
            attention: Yup.string()
                .label(l10n.getString("support-create-entity-error-label-attention"))
                .when("isSystemOwner", {
                    is: (val: AgreementOptions) => val === AgreementOptions.YES,
                    then: (schema) => schema.required(),
                    otherwise: (schema) => schema.optional(),
                }),
            reminderEmail: Yup.string()
                .label(l10n.getString("support-create-entity-error-label-reminder-email"))
                .when("isSystemOwner", {
                    is: (val: AgreementOptions) => val === AgreementOptions.YES,
                    then: (schema) => schema.required().email(),
                    otherwise: (schema) => schema.optional(),
                }),
            poNumber: Yup.string()
                .label(l10n.getString("support-create-entity-error-label-po-number"))
                .when("isSystemOwner", {
                    is: (val: AgreementOptions) => val === AgreementOptions.YES,
                    then: (schema) => schema.required(),
                    otherwise: (schema) => schema.optional(),
                }),
        })

        function handleSubmit(values: AccountFromValues) {
            onSubmit(values)
        }

        return (
            <Formik<AccountFromValues>
                enableReinitialize
                initialValues={initialValues ? initialValues : defaultInitialValues}
                validationSchema={supplierSchema}
                onSubmit={handleSubmit}
                innerRef={ref}
            >
                <Form>
                    <AccountUpdateContent history={history} />
                </Form>
            </Formik>
        )
    },
)
